import { reactive, computed } from '@nuxtjs/composition-api';
import { StateInterface, UseLandingInterface } from '~/composables/useLanding/useLanding';
import { PLANS_HORIZONTAL } from "~/utils/constants/landings";
import { DURATION_SPAN_MONTHLY } from '~/utils/constants/product';
import { GenericObjectType } from '~/utils/types';
import { Landing } from '~/utils/types/Landing';
import { PlanItem } from '~/utils/types/Plan';
import { toggleModule, setPaperAdded, setCrossSellAdded, setChoosenShippingMethod, removeModule, emptyModules,setAcceptedQuantities,setPaymentFrequencyChoosen, addModule, setSelectedShippingKey } from '~/utils/helpers/composables';

const state = reactive<StateInterface>({
  landing: null,
  plansLayout: PLANS_HORIZONTAL as string,
  paperAdded: [],
  crossSellAdded: {},
  choosenShippingMethod: {},
  selectedShippingKey: {},
  choosenModules: new Map(),
  modulesKeys: [], // We need this key map because the state can't watch complex objects as Map, using the keys as position-sku to make the sorting faster
  acceptedQuantities: 0,
  layoutSelectedPlan: null,
  selectedPlan : null,
  paymentFrequencyChoosen: null,
  triggerChangeHelper: false,
  userHasSetFrequency: false
});

/**
 * Global store for managing UI state.
 *
 * See the {@link UseUiStateInterface} for a list of methods and values available in this composable.
 */
export function useLanding(): UseLandingInterface {
  
  const setLayoutSelectedPlan = (val: number) => {
    state.layoutSelectedPlan = val;
    state.selectedPlan = state.landing.plans.items.find(plan => plan.id == val);
  };

  const getLowPaymentFrequency = ():string => {
    try {
      if (!state.selectedPlan || !state.selectedPlan.paymentFrequencies) {
        return DURATION_SPAN_MONTHLY;
      }
      return state.selectedPlan['paymentFrequencies'][0]
    } catch (e) {
      console.log(`Error in useLandings > getLowPaymentFrequency, returning M as default: ${e}`)
      return DURATION_SPAN_MONTHLY;
    }
  }
  const getHighPaymentFrequency = ():string => {
    try {
      if (!state.selectedPlan || !state.selectedPlan.paymentFrequencies) {
        return getLowPaymentFrequency();
      }
      return state.selectedPlan.paymentFrequencies[1] ?? getLowPaymentFrequency();
    } catch (e) {
      console.log(`Error in useLandings > getHighPaymentFrequency, returning low frequency as default: ${e}`)
      return getLowPaymentFrequency()
    }
  }

  const setLanding = (val: Landing, inEvidenceSku?:string) => {
    state.landing = val;
    state.userHasSetFrequency = false; // reset ad ogni nuovo landing
    /** Init state */
    if (!val.plans?.items || val.plans.items.length === 0) {
      console.error('setLanding: no plans available');
      return;
    }
    const preSelectedPlan = inEvidenceSku ? val.plans.items.find(el => el.sku==inEvidenceSku) : val.plans.items.find(el => !!el.preselected);
    const preSelectedPlanId = preSelectedPlan ? preSelectedPlan.id : val.plans.items[0]?.id;
    if (preSelectedPlanId) {
      setLayoutSelectedPlan(preSelectedPlanId);
    }
    setAcceptedQuantities(state, preSelectedPlan?.modulesInCartQty?.min),
    setPaymentFrequencyChoosen(state, getHighPaymentFrequency, getHighPaymentFrequency())
  };

  const getPlansItems = ():PlanItem[] => {
    try{return state.landing.plans.items}catch(e){return null}
  }

  const getLayoutSelectedPlan = (): number | null => {
    try {
      return state.landing.plans.items.find(el => el.preselected).id;
      
    } catch (e) {
      try{
        return state.landing.plans.items[0].id;
      }catch(e){
        console.log('Error getting preselected plan in useLanding > getLayoutSelectedPlan');
        return null;
      }
    }
  }

  /** For landings we manage only the paper version adding as per UX
   * @TODO generalize
   */
  const getBundleChoosenConfigurableProducts = () => {
    if (!state.landing) return [];
    /** Forcing recalculating because the computed not works with choosenShippingMethod as this is an object (avoid to create another map). Maybe we can detach the modulesKeys/ModulesSkus mapping with this trick. */
    state.triggerChangeHelper;
    
    const bundleSkus = [];
    
    // Aggiungi cartaceo se selezionato
    if (state.paperAdded.includes(state.layoutSelectedPlan) && state.choosenShippingMethod[state.layoutSelectedPlan]) {
      const bundle = state.landing.plans.items.find(el => el.id == state.layoutSelectedPlan);
      const paperOptionKey = Object.keys(bundle.deliveryCosts.costs).find(key => bundle.deliveryCosts.costs[key].uid == state.choosenShippingMethod[state.layoutSelectedPlan])
      if (paperOptionKey) {
        bundleSkus.push(bundle.deliveryCosts.costs[paperOptionKey].sku);
      }
    }
    
    // NOTA: il cross-sell (prodotto semplice) NON va qui: bundle-options non viene consumato da AddToCart.vue,
    // viene aggiunto separatamente via addItem in handleAddToCartWithCrossSell (ConfigureProductModal/NT).
    
    return bundleSkus;
  }

  const getCrossSellItems = () => {
    if (!state.landing) return [];
    state.triggerChangeHelper;
    
    const crossSellItems = [];
    
    if (Object.keys(state.crossSellAdded).some(k => state.crossSellAdded[k])) {
      const currentPlan = state.landing.plans.items.find(el => el.id == state.layoutSelectedPlan);
      if (currentPlan && currentPlan.configurableProducts) {
        const frequencyToTipoAbbonamento = {
          M: '1',
          Q: '3',
          Y: '12'
        };
        const tipoAbbonamento = frequencyToTipoAbbonamento[state.paymentFrequencyChoosen] || '1';
        
        Object.entries(state.crossSellAdded).forEach(([crossSellKey, isActive]) => {
          if (isActive && typeof crossSellKey === 'string') {
            const productSku = crossSellKey.split('-').slice(1).join('-');
            const crossSellProduct = currentPlan.configurableProducts.find(
              cp => cp.sku === productSku && cp.title === 'cross-sell'
            );
            
            if (crossSellProduct && crossSellProduct.variants) {
              const variant = crossSellProduct.variants.find(
                v => v.product.tipo_abbonamento === tipoAbbonamento
              );
              
              if (variant && crossSellProduct.configurable_options) {
                const tipoAbbonamentoOption = crossSellProduct.configurable_options.find(
                  opt => opt.attribute_code === 'tipo_abbonamento'
                );
                
                if (tipoAbbonamentoOption) {
                  crossSellItems.push({
                    parentSku: crossSellProduct.sku,
                    variantSku: variant.product.sku,
                    configurable_options: [{
                      attribute_id: tipoAbbonamentoOption.attribute_id,
                      attribute_code: 'tipo_abbonamento',
                      value_index: parseInt(tipoAbbonamento)
                    }]
                  });
                }
              }
            }
          }
        });
      }
    }
    
    return crossSellItems;
  }
  
  return {
    composableType: 'landing',
    landing: computed(() => state.landing),
    setLanding,
    plans: computed(() => state.landing ? state.landing.plans : null),
    getPlansLayout: () => state.landing.plansLayout,
    plansItems: computed(() => getPlansItems()),
    setPaperAdded: (val: {sku: string, active: boolean}) => setPaperAdded(state, val),
    paperAdded: computed(() => state.paperAdded),
    setCrossSellAdded: (val: {sku: string, active: boolean}) => setCrossSellAdded(state, val),
    crossSellAdded: computed(() => {
      state.triggerChangeHelper; // Force recalculation when crossSellAdded changes
      return state.crossSellAdded;
    }),
    setChoosenShippingMethod: (id, val)=>setChoosenShippingMethod(state, id, val),
    choosenShippingMethod: computed(() => state.choosenShippingMethod),
    setSelectedShippingKey: (sku, skuValueIndex)=>setSelectedShippingKey(state, sku, skuValueIndex),
    selectedShippingKey: computed(() => state.selectedShippingKey),
    addModule: (val: GenericObjectType) => addModule(state, val),
    removeModule: (val: GenericObjectType) => removeModule(state, val),
    toggleModule: (val:GenericObjectType) => toggleModule(state, val),
    getChoosenModules: () => state.choosenModules,
    modulesKeys: computed(() => state.modulesKeys as Array<String>),
    setAcceptedQuantities: (qty: number) => setAcceptedQuantities(state, qty),
    emptyModules: ()=>emptyModules(state),
    layoutSelectedPlan: computed(()=>state.layoutSelectedPlan ?? getLayoutSelectedPlan()),
    setLayoutSelectedPlan,
    paymentFrequencyChoosen: computed(() => state.paymentFrequencyChoosen),
    setPaymentFrequencyChoosen : (val: string) => setPaymentFrequencyChoosen(state, getHighPaymentFrequency, val),
    setUserHasSetFrequency: (val: boolean) => { 
      state.userHasSetFrequency = val; 
    },
    userHasSetFrequency: computed(() => state.userHasSetFrequency),
    selectedPlanLowPaymentFrequency : computed(()=>getLowPaymentFrequency()),
    selectedPlanHighPaymentFrequency: computed(() => getHighPaymentFrequency()),
    getField: (key: string) => state.landing[key],
    mainColor: computed(() => state.landing.mainColor),
    mainTextColor: computed(() => state.landing.mainTextColor),
    showPaymentFrequencySwitcher: computed(() => getLowPaymentFrequency() != getHighPaymentFrequency()),
    bundleChoosenConfigurableProducts: computed(() => getBundleChoosenConfigurableProducts()),
    crossSellItems: computed(() => getCrossSellItems()),
    hidePlansSelector: computed(() => state.landing.isSpecial || state.landing.plansLayout != PLANS_HORIZONTAL),
    selectedPlan : computed(()=>state.selectedPlan),
    revertOrderCard: computed(()=>state.landing.revertOrderCard),
    frequency_toggle: computed(()=>state.landing.frequency_toggle),
    otherProducts: computed(() => state.landing?.otherProducts || []),
    exit_intent_enabled: computed(()=>state.landing.exit_intent_enabled),
    exit_intent_title: computed(()=>state.landing.exit_intent_title),
    exit_intent_description: computed(()=>state.landing.exit_intent_description),
    exit_intent_button: computed(()=>state.landing.exit_intent_button),
    exit_intent_link: computed(()=>state.landing.exit_intent_link),
      adv_banner_active: computed(()=>state.landing.adv_banner_active),
      adv_banner_background: computed(()=>state.landing.adv_banner_background),
      adv_banner_title: computed(()=>state.landing.adv_banner_title),
      adv_banner_description: computed(()=>state.landing.adv_banner_description),
      adv_banner_label: computed(()=>state.landing.adv_banner_label),
      adv_banner_cta: computed(()=>state.landing.adv_banner_cta),
      adv_banner_icon: computed(()=>state.landing.adv_banner_icon),
    landingEntryBillingPeriod: computed(()=>state.landing.landingEntryBillingPeriod),
    discounted_toggle_low_frequency: computed(()=>state.landing.discounted_toggle_low_frequency),
    discounted_toggle_high_frequency: computed(()=>state.landing.discounted_toggle_high_frequency),
    verticalMobileVersion: computed(()=>state.landing.verticalMobileVersion),
    labelDescriptionFooter: computed(()=>state.landing?.labelDescriptionFooter),
    sole24CrosssellProducts: computed(()=>state.selectedPlan?.sole24_crosssell_products),
    textHighlighted: computed(()=>state.landing.textHighlighted)
  }
}

export function resetLandingModalState() {
  state.paperAdded = [];
  state.choosenShippingMethod = {};
  state.selectedShippingKey = {};
  state.choosenModules = new Map();
  state.crossSellAdded = {};
}

//selectedPlanLowPaymentFrequency!=selectedPlanHighPaymentFrequency
export default useLanding;
export * from './useLanding';
